import React from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { StudentTransactionMatrixItem } from '../../../types/paymentScheme';
import { IconButton, Tooltip } from '@mui/material';
import SvgIcons from '../../../components/icons/SvgIcons';
import { PaymentUser } from '../../../types/userType';
import { AdminUserRoles } from '../../../enums';
import { superAdminUsers } from '../../../constants/superAdminUsers';

interface PaymentDetailsColumnCallbacks {
  onUpdateStatusClick?: (transaction: StudentTransactionMatrixItem) => void;
  onViewReceiptClick?: (transaction: StudentTransactionMatrixItem) => void;
  onDownloadReceiptClick?: (transaction: StudentTransactionMatrixItem) => void;
  onSendReceiptClick?: (transaction: StudentTransactionMatrixItem) => void;
  onCancelPaymentClick?: (transaction: StudentTransactionMatrixItem) => void;
}

export const getPaymentDetailsColumns = (
  paymentUser: PaymentUser,
  callbacks?: PaymentDetailsColumnCallbacks
): GridColDef[] => {
  console.log(
    `Payment User Details for Payment Details Table: ${paymentUser?.email}`
  );
  const baseColumns: GridColDef[] = [
    { field: 'paymentDate', headerName: 'Payment Date', width: 150 },
    { field: 'amount', headerName: 'Amount', type: 'number', width: 100 },
    { field: 'particulars', headerName: 'Installment', width: 180 },
    { field: 'academicYear', headerName: 'Academic Year', width: 120 },
    {
      field: 'status',
      headerName: 'Payment Status',
      width: 120
    },
    { field: 'modeOfPayment', headerName: 'Mode of Payment', width: 150 },
    { field: 'receiptNo', headerName: 'Receipt No.', width: 220 },
    { field: 'against', headerName: 'Transaction Against', width: 200 },
    {
      field: 'transactionId',
      headerName: 'Transaction ID',
      width: 200
    },
    {
      field: 'email',
      headerName: 'Updated By',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentTransactionMatrixItem, boolean>
      ) => {
        return params.row.updatedByUser
          ? params.row.updatedByUser.email
          : 'N/A';
      }
    },
    { field: 'createdAt', headerName: 'Created On', width: 180 },
    {
      field: 'viewReceiptAction',
      headerName: 'View Receipt',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentTransactionMatrixItem>
      ) => (
        <Tooltip title='View'>
          <IconButton
            size='small'
            aria-label='view-receipt'
            onClick={() => callbacks?.onViewReceiptClick?.(params.row)}
          >
            <SvgIcons.Eye />
          </IconButton>
        </Tooltip>
      )
    },
    {
      field: 'downloadReceiptAction',
      headerName: 'Download Receipt',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentTransactionMatrixItem>
      ) => (
        <Tooltip title='Download'>
          <IconButton
            size='small'
            aria-label='download-receipt'
            onClick={() => callbacks?.onDownloadReceiptClick?.(params.row)}
          >
            <SvgIcons.ImportBlack />
          </IconButton>
        </Tooltip>
      )
    },
    {
      field: 'sendReceiptAction',
      headerName: 'Send Receipt',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentTransactionMatrixItem>
      ) => (
        <Tooltip title='Send'>
          <IconButton
            size='small'
            aria-label='send-receipt'
            onClick={() => callbacks?.onSendReceiptClick?.(params.row)}
          >
            <SvgIcons.Send />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const customColumns: GridColDef[] = [];

  if (
    paymentUser?.role === AdminUserRoles.FEE_ADMIN &&
    (!superAdminUsers.includes(paymentUser.email) ||
      paymentUser.email === 'stellar@universal.edu.in')
  ) {
    customColumns.push({
      field: 'updateStatusAction',
      headerName: 'Update Payment Status',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentTransactionMatrixItem>
      ) => (
        <Tooltip title='Update'>
          <IconButton
            size='small'
            aria-label='update-status'
            onClick={() => callbacks?.onUpdateStatusClick?.(params.row)}
          >
            <SvgIcons.Edit />
          </IconButton>
        </Tooltip>
      )
    });
  }
  if (paymentUser && paymentUser.role === AdminUserRoles.FEE_ADMIN) {
    customColumns.push({
      field: 'cancelPaymentAction',
      headerName: 'Cancel Payment',
      width: 180,
      renderCell: (
        params: GridRenderCellParams<StudentTransactionMatrixItem>
      ) => (
        <Tooltip title='Cancel'>
          <IconButton
            size='small'
            aria-label='cancel-payment'
            color='error'
            onClick={() => callbacks?.onCancelPaymentClick?.(params.row)}
          >
            <SvgIcons.CancelPayment />
          </IconButton>
        </Tooltip>
      )
    });
  }

  return [...baseColumns, ...customColumns];
};
